import { useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";

import { pageSearchField, typeInto } from "../components/EstateAskMic";
import { askText } from "../components/estateVoice";
import { hrefFor, pageById, pageByName, siteMapJson } from "./siteMap";
import { pageTools } from "./pageTools";
import dashboardTools from "./dashboardTools";
import paletteTools from "./paletteTools";

/**
 * The product, offered to whatever is driving the browser.
 * ---------------------------------------------------------------------------
 * WebMCP is a page saying what it can do, so an agent in the browser can do it
 * rather than guess at the DOM. The draft puts it on `document.modelContext`
 * and takes one tool at a time:
 *
 *     await document.modelContext.registerTool({
 *       name, description, inputSchema, async execute(args) {
 *         return { content: [{ type: "text", text: "..." }] };
 *       }
 *     })
 *
 * ...and an earlier shape of the same idea took the whole set at once
 * (`provideContext({ tools })`). Both are tried, in that order, because this
 * is a draft and the client is somebody else's.
 *
 * Three tools, which is the whole of "take me to X": what pages there are,
 * what one of them can do, and go there. They are the catalogue's own - a tool
 * only ever takes a page ID FROM the catalogue, never a path, so nothing that
 * can call a tool can steer the app somewhere the catalogue does not list.
 *
 * And where there is no client at all, the same catalogue is still published -
 * `window.appfactor.siteMap`, and a JSON block in the document - so something
 * that can only read a page can still find out what the page offers.
 * ---------------------------------------------------------------------------
 */

const MANIFEST_ID = "appfactor-site-map";

/* How long to wait for a page to bring its search field up. The route changes
   at once; the page behind it is lazy, and a cold one has taken seconds. Asked
   repeatedly rather than once after a delay - one timer that fires early drops
   the words silently, which is the worst of both. */
const WAIT_FOR_FIELD = 8000;
const ASK_EVERY = 180;

const say = (text) => ({ content: [{ type: "text", text }] });

/**
 * The search field of the page being navigated TO, once it exists.
 *
 * Three things have to be true, and the third was learnt the hard way: the
 * address has to be the destination's, the field has to be there - and it must
 * not be the field of the page being LEFT. React Router changes the address
 * synchronously and the old page is still mounted for a moment afterwards, so
 * a search typed on the strength of the first two lands in a field that is
 * about to be taken away, and nobody ever sees the words. Hence `not`: the
 * field as it was before the journey started.
 */
export function whenSearchField(expect, { not = null, timeout = WAIT_FOR_FIELD } = {}) {
  const started = Date.now();
  return new Promise(resolve => {
    const look = () => {
      const here = typeof window !== "undefined" ? window.location.pathname : "";
      const arrived = !expect || here === expect;
      const field = arrived ? pageSearchField() : null;
      if (field && field.isConnected && (!not || field !== not)) return resolve(field);
      if (Date.now() - started >= timeout) return resolve(null);
      setTimeout(look, ASK_EVERY);
    };
    setTimeout(look, ASK_EVERY);
  });
}

/** The catalogue in the document, for a reader that cannot call anything. */
function publish(map) {
  if (typeof document === "undefined") return;
  let tag = document.getElementById(MANIFEST_ID);
  if (!tag) {
    tag = document.createElement("script");
    tag.type = "application/json";
    tag.id = MANIFEST_ID;
    document.head.appendChild(tag);
  }
  tag.textContent = JSON.stringify(map);
  window.appfactor = { ...(window.appfactor || {}), siteMap: map };
}

/* The tools as they stand, for anything in the app that wants to act through
   the same three doors an outside agent uses - the butler, in particular. One
   set: what it calls is what a client would have called. */
let live = [];
export const siteToolsNow = () => live;

/** What the page says about offering itself, where anyone can read it. */
export const mcpLog = (...what) => {
  try { console.log("%c[mcp]", "color:#ff5e19;font-weight:700", ...what); }
  catch (e) { /* a console that will not take it is not worth failing over */ }
};

/**
 * Every place a client might be listening, and they are not the same place.
 * ---------------------------------------------------------------------------
 * The draft puts it on `document.modelContext`; an earlier shape of the same
 * idea, and the polyfill most extensions ship, puts it on `navigator`. Both
 * can be there at once - an extension's shim on one, the browser's own on the
 * other - and they are then two different registries. Registering with the
 * first one found was how a page could report three tools registered and still
 * show an empty panel: the panel reads the browser's registry, and the tools
 * had gone to the shim.
 *
 * So all of them get the tools, deduplicated by identity rather than by name,
 * since the same object is often reachable two ways.
 */
function surfaces() {
  const out = [];
  const add = (where, mc) => {
    if (!mc || out.some(s => s.mc === mc)) return;
    out.push({ where, mc });
  };
  if (typeof document !== "undefined") add("document.modelContext", document.modelContext);
  if (typeof navigator !== "undefined") add("navigator.modelContext", navigator.modelContext);
  if (typeof window !== "undefined") add("window.modelContext", window.modelContext);
  return out;
}

/** Is this the browser's own, or something standing in for it? */
function native(mc) {
  try { return /\[native code\]/.test(Function.prototype.toString.call(mc.registerTool || mc.provideContext)); }
  catch (e) { return false; }
}

/* How often to look for a client, and for how long. Looking once was the bug:
   `modelContext` is not always there when a page loads - an extension attaches
   when it attaches, and a panel that provides it may not be open yet - so a
   single check at mount meant a page that was offering nothing for the rest of
   its life, with nothing said about it. */
const LOOK_EVERY = 500;
const LOOK_FOR = 60000;

/**
 * Hand the tools over, one at a time, and keep what happened to each.
 *
 * Independently: a client that refuses one name - a duplicate from a hot
 * reload, a schema it does not like - used to take the other two down with it,
 * because one throw left the whole loop. And twice, if need be: the draft's
 * second argument is an options bag, and a build that does not know about it
 * can throw on the call rather than ignore it.
 */
async function register(mc, tools, signal) {
  const done = [];
  const failed = [];

  if (typeof mc.registerTool === "function") {
    for (const tool of tools) {
      try {
        if (signal) {
          try { await mc.registerTool(tool, { signal }); }
          catch (e) { await mc.registerTool(tool); }
        } else {
          await mc.registerTool(tool);
        }
        done.push(tool.name);
      } catch (e) {
        failed.push(`${tool.name}: ${e?.message || e}`);
      }
    }
    return { how: "registerTool", done, failed };
  }

  if (typeof mc.provideContext === "function") {
    /* the whole set at once, which replaces whatever was there - so this is
       said once, with every tool in it */
    try {
      await mc.provideContext({ tools });
      return { how: "provideContext", done: tools.map(t => t.name), failed };
    } catch (e) {
      return { how: "provideContext", done: [], failed: [String(e?.message || e)] };
    }
  }

  return {
    how: "unknown",
    done: [],
    failed: [`modelContext has neither registerTool nor provideContext (${Object.keys(mc).join(", ") || "no own keys"})`],
  };
}

/**
 * ...as soon as there is anything to hand them to, and to each thing once.
 *
 * It keeps looking for the whole minute rather than stopping at the first
 * client: a second one can arrive later - the browser's own, after an
 * extension's shim - and a page that stopped listening would never offer it
 * anything.
 */
function offerWhenReady(tools, signal, after) {
  const started = Date.now();
  const served = new Set();
  let stopped = false;
  let told = false;

  const look = async () => {
    if (stopped || signal?.aborted) return;

    for (const { where, mc } of surfaces()) {
      if (served.has(mc)) continue;
      served.add(mc);
      const result = await register(mc, tools, signal);
      if (stopped || signal?.aborted) return;
      told = true;
      after({ ...result, where, native: native(mc) });
    }

    if (Date.now() - started >= LOOK_FOR) {
      if (!told) after({ how: "none", where: null, done: [], failed: [] });
      return;
    }
    setTimeout(look, LOOK_EVERY);
  };

  look();
  return () => { stopped = true; };
}

/**
 * The tools themselves. `go` is handed in rather than closed over, so the set
 * registered once at startup still navigates with the router that is live now.
 */
export function siteTools(go, map) {
  /* The pages THIS installation has - Forge off, and there is no wiki to be
     told about or sent to. */
  const names = map.pages.map(p => p.id);
  const here = (id) => {
    const found = pageById(id) || pageByName(id);
    return found && names.includes(found.id) ? found : null;
  };

  return [
    /* What the page in front of the user is, and what can be done to it. The
       three below are about the product as a whole; these are about the thing
       on screen, which is most of what anybody actually asks about. */
    ...pageTools(say, map),
    ...dashboardTools(say, go),
    ...paletteTools(say),

    {
      name: "list_pages",
      description:
        "List the pages of this AppFactor installation: what each one answers, "
        + "the views it can be drawn in, and the filters it accepts. Call this first "
        + "to find the page id that navigate_to_page needs.",
      inputSchema: {
        type: "object",
        properties: {
          query: { type: "string", description: "Optional. Only pages matching these words." },
        },
      },
      async execute({ query } = {}) {
        const q = String(query || "").trim().toLowerCase();
        const pages = q
          ? map.pages.filter(p => `${p.id} ${p.label} ${p.what} ${(p.also || []).join(" ")}`.toLowerCase().includes(q))
          : map.pages;
        return say(JSON.stringify({ sections: map.sections, pages }, null, 2));
      },
    },

    {
      name: "describe_page",
      description: "Everything one page offers: its address, its views, and every filter it accepts.",
      inputSchema: {
        type: "object",
        properties: {
          page: { type: "string", description: `A page id. One of: ${names.join(", ")}` },
        },
        required: ["page"],
      },
      async execute({ page } = {}) {
        const found = map.pages.find(p => p.id === page) || null;
        if (!found) return say(`No page called "${page}". The pages are: ${names.join(", ")}`);
        return say(JSON.stringify(found, null, 2));
      },
    },

    {
      name: "navigate_to_page",
      description:
        "Take the user to a page of this application, optionally in a particular view, "
        + "with filters applied and a search already typed in.",
      inputSchema: {
        type: "object",
        properties: {
          page: { type: "string", description: `Which page. A page id from list_pages: ${names.join(", ")}` },
          view: { type: "string", description: "Optional. A view id from that page, e.g. \"cards\"." },
          search: { type: "string", description: "Optional. Words to look for once there." },
          filters: {
            type: "object",
            description: "Optional. Filter id to value, e.g. { \"severity\": \"critical\" } or { \"lens\": \"vulnerable\" }.",
          },
        },
        required: ["page"],
      },
      async execute({ page, view, search, filters } = {}) {
        /* only ever a page the catalogue lists, and only one this installation
           actually has: an id, or a name it answers to. Never a path handed in
           from outside. */
        const found = here(page);
        if (!found) return say(`No page called "${page}". The pages are: ${names.join(", ")}`);

        const asked = { ...(filters || {}) };
        if (search) asked.search = search;
        const { href, left } = hrefFor(found, { view, filters: asked });

        /* the field as it stands before the journey, so the one that is waited
           for afterwards is the destination's and not this page's */
        const leaving = pageSearchField();
        const staying = typeof window !== "undefined" && window.location.pathname === href.split("?")[0];
        go(href);

        /* A search the address cannot carry is typed into the page's own field
           once it is there, which is what someone would have done with it. */
        let typed = false;
        if (left.includes("search") && search) {
          /* ...unless the destination IS this page, where the field someone is
             already looking at is the right one */
          const field = await whenSearchField(href.split("?")[0], { not: staying ? null : leaving });
          if (field) { typeInto(field, search); typed = true; }
        }

        const undone = left.filter(id => id !== "search" || !typed);
        return say(JSON.stringify({
          went: href,
          page: found.id,
          applied_on_the_page: typed ? ["search"] : [],
          not_applied: undone,
          note: undone.length
            ? "Those filters live in the page's own controls; the page is open at the right place for them."
            : "",
        }, null, 2));
      },
    },
  ];
}

/**
 * Said once, from inside the router.
 *
 * Mounted next to the routes rather than on the catalogue page: "ask anywhere
 * to go anywhere" is only true if the tools are there on every page.
 */
export default function SiteMcp({ forge = true, deploy = true }) {
  const navigate = useNavigate();
  const go = useRef(navigate);
  go.current = navigate;

  useEffect(() => {
    const map = siteMapJson({ forge, deploy });
    publish(map);

    const tools = siteTools((href) => go.current(href), map);
    live = tools;
    const taking = new AbortController();

    /* The same three, callable by hand, whatever happens below: a console, a
       client that speaks no MCP, a test. Set BEFORE the offer, so they are
       there to be called while it is still looking for somebody to offer
       them to. */
    window.appfactor = {
      ...(window.appfactor || {}),
      siteMap: map,
      tools: tools.map(t => t.name),
      webmcp: "looking",
      call: (name, args) => {
        const tool = tools.find(t => t.name === name);
        return tool ? tool.execute(args || {}) : Promise.resolve(say(`No tool called "${name}"`));
      },
      /* ...and the front door beside them: the same sentence the ask takes,
         sent the same way, so what the butler does with it can be watched
         from a console without a microphone in the room. */
      ask: (what) => askText(what),
    };

    mcpLog(
      "offering", tools.length, "tools ·",
      `document.modelContext: ${typeof document !== "undefined" && document.modelContext ? "yes" : "no"}`,
      `· navigator.modelContext: ${typeof navigator !== "undefined" && navigator.modelContext ? "yes" : "no"}`,
    );

    const where = [];
    const stop = offerWhenReady(tools, taking.signal, (r) => {
      if (taking.signal.aborted) return;
      if (r.where) where.push(`${r.where}${r.native ? "" : " (a stand-in, not the browser's own)"}`);
      window.appfactor = {
        ...(window.appfactor || {}),
        webmcp: r.how,
        webmcpWhere: [...where],
        webmcpTools: r.done,
        webmcpFailed: r.failed,
      };

      if (r.how === "none") {
        mcpLog(
          "no modelContext on document, navigator or window - nothing to register with.",
          "The catalogue is still on window.appfactor.siteMap, in #appfactor-site-map,",
          "and every tool is callable by hand: window.appfactor.call(\"list_pages\")",
        );
      } else if (r.done.length) {
        mcpLog(
          `registered through ${r.how} on ${r.where}`,
          r.native ? "(the browser's own)" : "(a stand-in - if a panel shows nothing, this is why)",
          "·", r.done.join(", "),
        );
      }
      if (r.failed.length) mcpLog("refused by", r.where, "·", r.failed.join(" · "));
    });

    /* ...and taken back when this goes, so the next mount is offering them to
       a client that is not already holding them. */
    return () => { stop(); taking.abort(); };
  }, [forge, deploy]);

  return null;
}
